import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { formatText } from "@shared/ui/format";
import type { EventComponentType } from "@shared/lib/google-analytics-4.type";
import { JsonDataBuilder } from "@shared/lib/JsonDataBuilder";
import { logError } from "@shared/lib/sentry-error";
import type { ReactNode } from "react";
import type { PensionSavingLeadFormFields } from "../LeadForm/PensionSavingLeadForm";
import { PensionSavingLeadForm } from "../LeadForm/PensionSavingLeadForm";
import type { PensionLeadCalcRequest, PensionLeadCalcResponse } from "../LeadForm/types";
import { DEFAULT_MONTHLY_DEPOSIT } from "./data";
import { calculate, getSearchData } from "./domain";
import * as Step from "./Steps";
import "./style.scss";

export interface FormFields extends PensionSavingLeadFormFields {
	birthYear: number;
	monthlyDeposit: number;
	requestConsultantCall?: string;
}

type Props = AbstractLeadFormProps<PensionLeadCalcRequest, PensionLeadCalcResponse>;
interface State extends AbstractLeadFormState<FormFields> {
	calculatorResponse: PensionLeadCalcResponse;
}

/**
 * @Shortcode
 *
 * @title "Nyugdíj kalkulátor"
 * @shortcode "pension_small"
 * @slug "nyugdij"
 * @type "calculator"
 */
export class PensionSavingsSmallCalculator extends PensionSavingLeadForm<
	PensionLeadCalcRequest,
	PensionLeadCalcResponse,
	FormFields,
	Props,
	State
> {
	// EventParams
	protected readonly productTypes: string = "SAVINGS_INSURANCE";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "",
		outsideTitle: "Nyugdíj-megtakarítás kalkulátor",
		outsideTitleLink: "/nyugdij-megtakaritas/",
		bottomLogo: true,
		colorScheme: "dark",
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			calculatorResponse: null,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			submitState,
			calculatorResponse: response,
			values: { email },
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="result" submitState={submitState} />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					canStepBack
					finalAmount={response !== null ? response.finalAmount : undefined}
				/>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					button="Elküldöm"
					submitState={submitState}
					colorVersion="white"
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private onResult(target: string): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(calculate(values))
			.then((response: PensionLeadCalcResponse) => {
				if (!response.finalPension) {
					this.setState({ submitState: "error" });

					this.promisePool.addTimeout().then(() => {
						this.setState({ submitState: undefined });
					});

					return;
				}

				this.setState(
					{
						calculatorResponse: response,
						submitState: undefined,
					},
					() => {
						super.onStep(target, true);
						this.sendViewItemListEvent();
					},
				);

				this.sendCalculationEvent();
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	protected getCalculatorRequest(): PensionLeadCalcRequest {
		const {
			values: { birthYear, monthlyDeposit, requestConsultantCall },
		} = this.state;

		const otherData = new JsonDataBuilder();
		otherData.addItem("Elért pontszám", "scoring", this.calcScoring());
		otherData.addItem("Kér tájékoztatást", "requestConsultantCall", requestConsultantCall ?? "Nem jelölt");

		return {
			...super.getCalculatorRequest(),
			birthYear: Number(birthYear),
			monthlyDeposit,
			otherDataJson: otherData.getJson(),
		};
	}

	protected getCalculatorResponse(): PensionLeadCalcResponse {
		const { calculatorResponse } = this.state;

		return {
			...super.getCalculatorResponse(),
			...calculatorResponse,
			scoring: this.calcScoring(),
		};
	}

	private calcScoring(): number {
		const {
			values: { monthlyDeposit },
		} = this.state;
		// Állította +1 pont
		const scoring = monthlyDeposit === DEFAULT_MONTHLY_DEPOSIT ? 0 : 1;

		return scoring;
	}

	protected getClassName(): string {
		return `${super.getClassName()} pension react-small-calculator`;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-pension";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { monthlyDeposit, birthYear },
			calculatorResponse: {
				finalAmount,
				subsidiesYield,
				cummulatedPayment,
				paymentsYield,
				cumulatedCost,
				finalPension,
				pensionIncrease,
				yearsUntilRetirement,
			},
		} = this.state;

		return {
			...super.getAutoresponderFields(),
			monthlyDeposit: formatText(monthlyDeposit, "ft"),
			finalAmount: formatText(finalAmount, "ft"),
			subsidiesYield: formatText(subsidiesYield, "ft"),
			cummulatedPayment: formatText(cummulatedPayment, "ft"),
			paymentsYield: formatText(paymentsYield, "ft"),
			cumulatedCost: formatText(cumulatedCost, "ft"),
			finalPension: formatText(finalPension, "ft"),
			pensionIncrease: formatText(pensionIncrease, "%"),
			yearsUntilRetirement,
			birthYear,
		};
	}

	protected getCalculationData(): string {
		const { values } = this.state;

		return JSON.stringify(getSearchData(values));
	}

	private sendViewItemListEvent(): void {
		const { calculatorResponse: response } = this.state;
		this.sendEvent("view_item_list", {
			item_list_id: this.calculationId,
			item_list_name: "normal",
			items: [
				{
					item_id: "",
					item_brand: "",
					item_name: "",
					affiliation: "product",
					index: 1,
					coupon: "none",
					currency: "HUF",
					discount: 0,
					item_category: "",
					item_category2: "",
					item_category3: "",
					item_category4: "",
					item_category5: "",
					item_variant: "",
					price: response !== null ? Math.floor(response.finalAmount) : undefined,
					quantity: 0,
				},
			],
		});
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.PensionSavingsSmallCalculator`;
	}
}

export default PensionSavingsSmallCalculator;
